import React from 'react';
import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuArrowLeftRight, LuTarget } from 'react-icons/lu';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useDisplayAmount } from '../../contexts/ConvertedAmountsContext';
import { formatRowAmount } from '../../utils/formatters';
import { dayShort, monthShort } from '../../utils/intlFormatters';
import { getMovementAlerts } from '../../utils/movementAlerts';
import { FALLBACK_GOAL_COLOR } from '../../utils/goals';
import { categoryTint } from '../../utils/categoryTint';
import {
  GROUP_COLORS, getAccountDisplay, getPrimaryText, isPrimaryTextRestricted,
} from '../../utils/movementDisplay';
import { GOAL_ICONS } from '../goals/GoalIcon';

const TONE_COLOR = {
  income: 'phone.income',
  expense: 'redesign.heading',
  transfer: 'phone.muted',
};

// At 320px (Display Zoom) the tags and the goal give way first; the account
// then truncates. The New pill shrinks to a dot, so the name keeps its room.
const NARROW = '@media screen and (max-width: 359.98px)';
const HIDE_WHEN_NARROW = { [NARROW]: { display: 'none' } };
const SHOW_WHEN_NARROW = { display: 'none', [NARROW]: { display: 'inline-block' } };

const WAITING = 'needsSourceFile';

// Line 2 has room for a few words, so each review reason has a short phone
// label; the sheet spells it out.
const SHORT_REASON = {
  halfConfirmed: 'halfConfirmed',
  accountMismatch: 'otherStatement',
  wrongCurrency: 'wrongCurrency',
  exchangeMissing: 'noExchangeRate',
  sourceRules: 'checkDirection',
};

// "Sun, Sep 27", as the day headers read, with the year when it is not this
// one: in a list that spans months the weekday tells repeats apart.
function rowDate(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const base = `${dayShort(new Date(y, m - 1, d).getDay())}, ${monthShort(m - 1)} ${d}`;
  return y === new Date().getFullYear() ? base : `${base}, ${y}`;
}

function Dot() {
  return <Box as="span" flexShrink={0} px="4px">·</Box>;
}

/** The 40px disc on the left: the category, a transfer, or "?" for none. */
function RowDisc({ movement, flag }) {
  const { colorMode } = useColorMode();
  const isTransfer = movement.type === 'Transfer';
  const category = movement.category;
  let disc;
  if (isTransfer) {
    disc = { bg: 'redesign.bg.accent', color: 'redesign.accent.assets', content: <Box as={LuArrowLeftRight} boxSize="20px" /> };
  } else if (!category) {
    disc = {
      border: '1.5px dashed', borderColor: 'phone.muted', color: 'phone.muted',
      content: <Text as="span" fontSize="17px" fontWeight="600">?</Text>,
    };
  } else {
    disc = {
      bg: category.color ? categoryTint(category.color, colorMode).bg : 'redesign.bg.card',
      content: <Text as="span" fontSize="19px" lineHeight="1">{category.icon || category.name?.charAt(0)}</Text>,
    };
  }
  const { content, ...style } = disc;

  return (
    <Flex
      gridRow="1 / 3"
      w="40px"
      h="40px"
      borderRadius="full"
      align="center"
      justify="center"
      position="relative"
      flexShrink={0}
      {...style}
    >
      {content}
      {flag && (
        <Flex
          position="absolute"
          right="-3px"
          bottom="-3px"
          w="16px"
          h="16px"
          borderRadius="full"
          border="2px solid"
          borderColor="redesign.bg.page"
          align="center"
          justify="center"
          fontSize={flag === 'alert' ? '9px' : '8px'}
          fontWeight="800"
          bg={flag === 'alert' ? 'phone.badgeBg' : 'redesign.bg.card'}
          color="phone.badgeFg"
          filter={flag === 'waiting' ? 'grayscale(1)' : undefined}
        >
          {flag === 'alert' ? '!' : '⏳'}
        </Flex>
      )}
    </Flex>
  );
}

/**
 * One movement on a phone list: 64px, two lines, one tap target.
 *
 * Line 1 is the category disc, the name and the amount. Line 2 is the
 * account, category, first tag and goal; when the movement needs review it
 * is the first reason instead, in the alert colour. A movement that is only
 * waiting for its bank row says so in muted grey: there is nothing to fix.
 * An ignored one starts line 2 with "Ignored ·". The name keeps at least 40%
 * of the row: a long foreign amount truncates before it.
 *
 * Screen readers get one composed label ("Coffee, expense, 18,000 COP, Card,
 * Eating out, new") instead of the parts.
 *
 * `groupColor` is the palette slot the list assigned to the movement's
 * group; without it the group id picks one.
 */
export default function PhoneMovementRow({
  movement,
  onClick,
  highlight = false,
  showDate = false,
  homeCurrency,
  groupColor,
}) {
  const { t } = useTranslation('movements');
  const { alerts: prefs, homeCurrency: preferredHome } = usePreferences();
  const home = homeCurrency ?? preferredHome;
  const display = useDisplayAmount(movement);

  const isTransfer = movement.type === 'Transfer';
  const restricted = isPrimaryTextRestricted(movement);
  const name = restricted ? t('list.fallback.restricted') : getPrimaryText(movement);
  const amount = formatRowAmount(movement, home);
  const approx = display.isConverted && movement.currency !== home
    ? `≈ ${formatRowAmount({ ...movement, amount: display.amount, currency: display.currency }, home).text}`
    : null;

  const alerts = getMovementAlerts(movement, { t, prefs });
  const fixable = alerts.filter((a) => a.key !== WAITING);
  const waitingOnly = alerts.length > 0 && fixable.length === 0;
  const reasonText = (alert) => {
    if (alert.key === 'incomplete') {
      if (!isTransfer && !movement.category_id) return t('phone.row.noCategory');
      if (!isTransfer && !movement.account_id && !movement.account) return t('phone.row.reasons.noAccount');
      return t('phone.row.reasons.incomplete');
    }
    return SHORT_REASON[alert.key] ? t(`phone.row.reasons.${SHORT_REASON[alert.key]}`) : alert.label;
  };

  const tags = (movement.tag_movements || []).map((tm) => tm.tag?.name).filter(Boolean);
  const account = getAccountDisplay(movement);
  const categoryName = !isTransfer && movement.category?.name;
  const goal = movement.goal;
  const GoalGlyph = goal ? (GOAL_ICONS[goal.icon] || LuTarget) : null;
  const rail = groupColor
    || (movement.movement_group_id ? GROUP_COLORS[movement.movement_group_id % GROUP_COLORS.length] : null);

  const ariaLabel = [
    name,
    t(`phone.row.types.${movement.type}`, { defaultValue: movement.type }),
    // Spoken without a sign (the type says it) and without a zero fraction.
    `${formatRowAmount({ type: 'Transfer', amount: movement.amount, currency: movement.currency }, movement.currency).text} ${movement.currency || ''}`.trim(),
    account,
    categoryName,
    goal?.name && t('phone.row.goal', { name: goal.name }),
    movement.is_new && t('phone.row.new'),
    movement.is_ignored && t('phone.row.ignored'),
    ...fixable.map(reasonText),
    waitingOnly && t('phone.row.waiting'),
  ].filter(Boolean).join(', ');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(movement);
    }
  };

  const dateTail = showDate && movement.date && (
    <><Dot /><Box as="span" flexShrink={0}>{rowDate(movement.date)}</Box></>
  );
  // A goal is a tail: the goal's icon and name, the first thing to truncate
  // after the tags, and gone at 320.
  const goalTail = goal && (
    <Box as="span" display="inline-flex" alignItems="center" minW={0} sx={HIDE_WHEN_NARROW}>
      <Dot />
      <Box as={GoalGlyph} boxSize="13px" flexShrink={0} mr="3px" color={goal.color || FALLBACK_GOAL_COLOR} />
      <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis">{goal.name}</Box>
    </Box>
  );

  let line2;
  if (fixable.length > 0) {
    // The reason alone: an account beside it would leave neither readable.
    line2 = (
      <>
        <Box as="span" color="phone.alert" minW={0} overflow="hidden" textOverflow="ellipsis">
          {reasonText(fixable[0])}
          {alerts.length > 1 && ` +${alerts.length - 1}`}
        </Box>
        {dateTail}
      </>
    );
  } else if (isTransfer) {
    line2 = (
      <>
        <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis">
          {movement.from_account?.name || t('fallback.unknown')}
        </Box>
        <Box as="span" flexShrink={0} px="4px">→</Box>
        <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis">
          {movement.to_account?.name || t('fallback.unknown')}
        </Box>
        {dateTail}
        {goalTail}
      </>
    );
  } else {
    line2 = (
      <>
        {waitingOnly && (
          <>
            <Box as="span" flexShrink={0} filter="grayscale(1)" opacity={0.8} mr="4px">⏳</Box>
            <Box as="span" flexShrink={0}>{t('phone.row.waiting')}</Box>
            <Dot />
          </>
        )}
        <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis">{account}</Box>
        {dateTail}
        {!showDate && !waitingOnly && categoryName && (
          <><Dot /><Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis">{categoryName}</Box></>
        )}
        {!waitingOnly && tags.length > 0 && (
          <Box as="span" display="inline-flex" flexShrink={0} sx={HIDE_WHEN_NARROW}>
            <Dot />#{tags[0]}{tags.length > 1 && ` +${tags.length - 1}`}
          </Box>
        )}
        {goalTail}
      </>
    );
  }

  return (
    <Box
      data-phone-row=""
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={() => onClick?.(movement)}
      onKeyDown={handleKeyDown}
      display="grid"
      gridTemplateColumns="40px minmax(40%, 1fr) minmax(0, max-content)"
      columnGap="12px"
      rowGap="2px"
      alignItems="center"
      alignContent="center"
      minH="64px"
      py="9px"
      px="16px"
      position="relative"
      cursor="pointer"
      bg={highlight ? 'redesign.bg.accent' : 'transparent'}
      transition="background-color 0.6s ease-out"
      opacity={movement.is_ignored ? 0.55 : 1}
      _active={{ bg: 'redesign.bg.hover' }}
      sx={{
        WebkitTapHighlightColor: 'transparent',
        '[data-phone-row] + &::before': {
          content: '""', position: 'absolute', top: 0, left: '68px', right: 0,
          height: '1px', bg: 'redesign.border',
        },
      }}
    >
      {rail && (
        <Box position="absolute" left="4px" top="10px" bottom="10px" w="3px" borderRadius="full" bg={rail} />
      )}
      <RowDisc movement={movement} flag={fixable.length ? 'alert' : waitingOnly ? 'waiting' : null} />

      <Flex align="center" minW={0}>
        <Text
          textStyle="phoneRowName"
          color={restricted ? 'phone.muted' : 'redesign.heading'}
          fontStyle={restricted ? 'italic' : 'normal'}
          textDecoration={movement.is_ignored ? 'line-through' : 'none'}
          isTruncated
        >
          {name}
        </Text>
        {movement.is_new && (
          <>
            <Box
              as="span"
              ml="6px"
              flexShrink={0}
              display="inline-flex"
              alignItems="center"
              h="18px"
              px="6px"
              borderRadius="9px"
              bg="phone.newPillBg"
              color="phone.newPillFg"
              fontSize="11px"
              fontWeight="600"
              sx={HIDE_WHEN_NARROW}
            >
              {t('list.badges.new')}
            </Box>
            <Box
              as="span"
              ml="6px"
              flexShrink={0}
              w="8px"
              h="8px"
              borderRadius="full"
              bg="phone.newPillFg"
              sx={SHOW_WHEN_NARROW}
            />
          </>
        )}
      </Flex>

      <Text
        textStyle="phoneRowAmount"
        color={TONE_COLOR[amount.tone]}
        textAlign="right"
        isTruncated
        textDecoration={movement.is_ignored ? 'line-through' : 'none'}
      >
        {amount.text}
      </Text>

      <Flex
        gridColumn={approx ? '2' : '2 / 4'}
        align="center"
        minW={0}
        textStyle="phoneMeta"
        color="phone.muted"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {movement.is_ignored && (
          <><Box as="span" flexShrink={0}>{t('list.badges.ignored')}</Box><Dot /></>
        )}
        {line2}
      </Flex>
      {approx && (
        <Text textStyle="phoneMeta" color="phone.muted" textAlign="right" isTruncated>
          {approx}
        </Text>
      )}
    </Box>
  );
}
